import {unwrapResult} from '@reduxjs/toolkit';
import React, {memo, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {UiAppointmentBookingLayout} from '~/components/elements/UiAppointmentBookingLayout';
import {APPOINTMENT_BOOKING_DESCRIPTIONS} from '~/constants/appointment-bookings';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {bookAppointment} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {setAppointmentSorryModal} from '~/redux/ui/slice';
import {requestOK} from '~/utils/thunk-api';
import {redirectInIframe} from '~/utils/helpers';
import {useHistory} from 'react-router-dom';
import {isPayloadWithErrorCode} from '~/constants/medical-questionnaires';
import * as storage from '~/utils/storage';

interface Props {}

const BcRenewalAppointmentBooking = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const userQuestionnaireId = useSelector((state: RootState) => state.questionnaires.userQuestionnaireId);
  const {orderId, userQuestionId, key, typeAppt, time} = useSelector(
    (state: RootState) => state.questionnaires.rebookingAppointment,
  );
  const isWebview = useSelector((state: RootState) => state.questionnaires.isWebview);
  const {loading} = useRequestState(bookAppointment);
  const history = useHistory();
  const [redirectLoading, setRedirectLoading] = useState(false);
  const {inputs, setInputs} = useInput({
    selectedDate: undefined,
    selectedTime: undefined,
  });
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  useEffect(() => {
    if (!wpAuthUser?.id && orderId && userQuestionId) {
      if (wpAuthUser?.baseURL && wpAuthUser?.currentURL) {
        redirectInIframe(
          wpAuthUser.baseURL +
            '/login/?redirect_to=' +
            wpAuthUser?.currentURL +
            '/?values=' +
            userQuestionId +
            '/' +
            orderId +
            '/' +
            typeAppt +
            '/' +
            key +
            '/' +
            time,
          '_self',
        );
      }
    }
  }, [time, key, orderId, typeAppt, userQuestionId, wpAuthUser?.baseURL, wpAuthUser?.currentURL, wpAuthUser?.id]);

  if (!wpAuthUser?.id && orderId) return null;

  const onChangeDate = (value?: Date) => {
    if (value) {
      setInputs({selectedDate: value});
    }
  };

  const onChangeTime = (value?: Date) => {
    setInputs({selectedTime: value});
  };

  const onClickSubmit = async () => {
    const alreadyBookedAppointment = storage.load(`${time}`);
    if (alreadyBookedAppointment && orderId) {
      history.push('/booked-appointment-fail');
      return;
    }
    const resAction = await dispatch(
      bookAppointment({
        data: {startTime: inputs.selectedTime, userQuestionnaireId: userQuestionnaireId || userQuestionId, orderId},
        platform: isWebview ? 1 : 2,
      }),
    );
    if (requestOK(resAction, bookAppointment)) {
      const result = unwrapResult(resAction);
      if (orderId) {
        await storage.save(`${time}`, true);
        history.push('/booked-appointment');
        return;
      }
      if (result.checkoutURL) {
        setRedirectLoading(true);
        redirectInIframe(result.checkoutURL);
      }
    } else {
      if (isPayloadWithErrorCode(resAction?.payload)) {
        if (resAction?.payload?.errStatusCode === 403) return;
      }
      if (orderId) {
        history.push('/booked-appointment-fail');
        return;
      }
      dispatch(setAppointmentSorryModal());
    }
  };

  return (
    <UiAppointmentBookingLayout
      descriptions={APPOINTMENT_BOOKING_DESCRIPTIONS.BC_RENEWAL_12_MONTHS}
      dateValue={inputs.selectedDate}
      timeValue={inputs.selectedTime}
      onChangeDate={onChangeDate}
      onChangeTime={onChangeTime}
      loading={loading || redirectLoading}
      timeSelectionTitle="Check-in slots available on"
      handleOnNext={onClickSubmit}
      disableNext={!inputs.selectedDate || !inputs.selectedTime || loading || redirectLoading}
      showNoSlotWantToBook
    />
  );
};

export default memo(BcRenewalAppointmentBooking);
